import { ReactNode } from "react";
import clsx from "clsx";

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Primary CTA, typically a button or `<Link>`. */
  action?: ReactNode;
  /** Optional secondary CTA below the primary one. */
  secondaryAction?: ReactNode;
  className?: string;
}

/**
 * Friendly placeholder used when a page would otherwise be empty. Tries to be
 * inviting rather than just dropping a "No data yet." line - we want users to
 * understand what they're about to set up and how to start.
 */
export default function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div className={clsx("card flex flex-col items-center gap-4 py-12 text-center", className)}>
      <div className="relative">
        <div className="absolute -inset-4 rounded-full bg-brand-500/10 blur-xl" aria-hidden="true" />
        <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500/20 to-fuchsia-500/10 text-brand-200 ring-1 ring-brand-400/30">
          {icon}
        </div>
      </div>
      <div className="space-y-1.5">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {description && (
          <p className="mx-auto max-w-md text-sm text-slate-400">{description}</p>
        )}
      </div>
      {(action || secondaryAction) && (
        <div className="flex flex-col items-center gap-2 sm:flex-row">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
